// BUILD-042 — Unified employee workspace (real product navigation).
// Authority: docs/superpowers/decisions/2026-10-06-build-042-approved-unified-employee-layout.md
//
// This is the REAL product's grouped section map — four groups, sixteen destinations, matching
// the Builder-approved visual reference exactly in labels/order/grouping. It is a SEPARATE
// module from pages/hr/preview/sectionGroups.ts, which stays strictly synthetic/visual-only (see
// that file's own header). This module is imported by MyHrTab.tsx (self) and HrAdminTab.tsx
// (HR Administration viewing an Employee) — the two REAL, server-authorized destinations.
//
// SectionReadiness distinguishes which of the 16 destinations have real working functionality
// today vs. are part of the approved design but not built yet — never inferred, always an
// explicit label (decision: "Future sections remain meaningful Planned/Preview; mock saves
// never claim real persistence").
export type SectionKey =
  | 'overview' | 'contacts' | 'employment' | 'benefits' | 'time'
  | 'training' | 'checklists' | 'property' | 'reviews'
  | 'documents' | 'custom' | 'history'
  | 'safety' | 'relations' | 'medical' | 'compliance';

export interface SectionNavItem {
  key: SectionKey;
  label: string;
}

export interface SectionNavGroup {
  groupLabel: string;
  items: SectionNavItem[];
}

export const SECTION_NAV_GROUPS: SectionNavGroup[] = [
  {
    groupLabel: 'Employee',
    items: [
      { key: 'overview', label: 'Overview' },
      { key: 'contacts', label: 'Personal & Contacts' },
      { key: 'employment', label: 'Employment & Pay' },
      { key: 'benefits', label: 'Benefits' },
      { key: 'time', label: 'Time Off' },
    ],
  },
  {
    groupLabel: 'Work & Development',
    items: [
      { key: 'training', label: 'Training & Qualifications' },
      { key: 'checklists', label: 'Onboarding & Offboarding' },
      { key: 'property', label: 'Equipment & Property' },
      { key: 'reviews', label: 'Reviews' },
    ],
  },
  {
    groupLabel: 'Records',
    items: [
      { key: 'documents', label: 'Documents & Signatures' },
      { key: 'custom', label: 'Additional Information' },
      { key: 'history', label: 'History & Corrections' },
    ],
  },
  {
    groupLabel: 'Restricted',
    items: [
      { key: 'safety', label: 'Safety & Incidents' },
      { key: 'relations', label: 'Employee Relations' },
      { key: 'medical', label: 'Medical & Protected Leave' },
      { key: 'compliance', label: 'Compliance' },
    ],
  },
];

export const ALL_SECTION_KEYS: SectionKey[] = SECTION_NAV_GROUPS.flatMap((g) => g.items.map((i) => i.key));

export function groupContaining(key: SectionKey): string {
  return SECTION_NAV_GROUPS.find((g) => g.items.some((i) => i.key === key))?.groupLabel ?? SECTION_NAV_GROUPS[0].groupLabel;
}

export function labelFor(key: SectionKey): string {
  return SECTION_NAV_GROUPS.flatMap((g) => g.items).find((i) => i.key === key)?.label ?? key;
}

/** One-line page subtitle shown under each destination's bold title (approved reference's
 *  "Your current information and next steps, in one place." style) — purely descriptive copy,
 *  never a disclosure of real vs. planned (that stays on the PlannedSection/readiness badge). */
const SECTION_SUBTITLES: Record<SectionKey, string> = {
  overview: 'Your current information and next steps, in one place.',
  contacts: 'Personal details, ways to reach you, and emergency contacts.',
  employment: 'Position, reporting structure, and employment terms.',
  benefits: 'Benefit information and your eligibility (not enrollment).',
  time: 'Balances, requests, and upcoming time away.',
  training: 'Training, qualifications, and professional memberships.',
  checklists: 'Assigned onboarding and offboarding tasks.',
  property: 'Equipment and property issued to you.',
  reviews: 'Performance reviews and feedback.',
  documents: 'Documents, acknowledgments, and signed copies.',
  custom: 'Additional approved information sections.',
  history: 'Changes, requests, and decisions.',
  safety: 'Incident records and safety follow-up.',
  relations: 'Employee-relations and investigation records.',
  medical: 'Medical documents and protected-leave details.',
  compliance: 'Compliance evidence and verification.',
};

export function subtitleFor(key: SectionKey): string {
  return SECTION_SUBTITLES[key];
}

/** Sections with real, working, server-backed (or genuinely locally-interactive, already-
 *  shipped) functionality today. Everything else renders as an honest Planned placeholder —
 *  never fabricated data, never a "Submitted"/"Saved" claim. Readiness is tracked PER CONTEXT
 *  because the same destination can be real for the Employee's own self-service view while
 *  still genuinely not wired for HR Administration viewing an arbitrary Employee (e.g.
 *  Training & Qualifications today only has My HR's own local-state UI — mounting it for an
 *  admin viewing someone ELSE's record would mislabel every "You" actor name in that component
 *  as the Employee rather than the admin, so it stays honestly Planned on the admin side until
 *  that is actually built). Never widen either set without the matching UI actually existing. */
export type WorkspaceViewerContext = 'self' | 'admin';

// BUILD-042 Owner-led onboarding (2026-10-07): 'documents' moves from Planned to real/built on
// BOTH sides — HR assigns/reviews (admin), the Employee views/downloads/uploads their own
// (self). Paper print/sign/scan/upload workflow only (no field authoring, no in-app typed-name
// signing, no generated PDFs) per the Owner's 23:23 simplification.
// BUILD-042 simple benefits (migration 207, 2026-10-22): 'benefits' moves from Planned to
// real/built on BOTH sides — the Employee sees published benefit info + their own eligibility
// (read-only self view); HR Administration sees and sets per-employee eligibility. Eligibility
// is a review outcome only, never enrollment.
const SELF_BUILT_SECTIONS: ReadonlySet<SectionKey> = new Set<SectionKey>([
  'overview', 'contacts', 'employment', 'benefits', 'training', 'checklists', 'documents', 'property', 'history',
]);
const ADMIN_BUILT_SECTIONS: ReadonlySet<SectionKey> = new Set<SectionKey>([
  'overview', 'contacts', 'employment', 'benefits', 'checklists', 'documents', 'property', 'history',
]);

export function isBuilt(key: SectionKey, context: WorkspaceViewerContext): boolean {
  return context === 'self' ? SELF_BUILT_SECTIONS.has(key) : ADMIN_BUILT_SECTIONS.has(key);
}

/** The Restricted group (Safety & Incidents / Employee Relations / Medical & Protected Leave /
 *  Compliance) has no real per-compartment permission signal anywhere in this frontend today
 *  (confirmed by investigation — HrAccessContext only resolves myHr/myTeam/hrAdmin surfaces, not
 *  compartment-level grants). Per the decision ("actual permissions govern protected data...
 *  HR Admin alone implies no restricted compartment grant"), inventing a visible-but-fake gate
 *  would be worse than omitting the group: this app never shows Restricted in the REAL
 *  workspace (self or admin) until a real compartment-grant signal exists to drive it. It
 *  remains reachable only through the explicitly-synthetic preview, exactly as today. */
export function isRestrictedGroup(groupLabel: string): boolean {
  return groupLabel === 'Restricted';
}

/** Convenience predicate for WorkspaceNav's `visible` prop: true for every section EXCEPT the
 *  ones in the Restricted group — see isRestrictedGroup's header for why Restricted is never
 *  shown in the real workspace today. */
export function isNonRestricted(key: SectionKey): boolean {
  return !isRestrictedGroup(groupContaining(key));
}

// ── Admin employee rail (Owner decision 2026-10-24, "Admin versus employee view") ───────────────────────────────────────────────
// HR administering ANOTHER person gets a rail with the working destinations visible directly (grouped below, see ADMIN_EMPLOYEE_RAIL_GROUPS)
// and a single collapsed "More" group that keeps EVERY other existing destination reachable (nothing is deleted; Planned stays Planned).
// The rail only chooses which existing section (and, for Onboarding / Offboarding, which existing tab) to show; it adds no data or permission.
// The employee's OWN (My HR) rail keeps the grouped map above.
export type ChecklistRailTab = 'onboarding' | 'offboarding';

export interface AdminRailItem {
  id: string;
  label: string;
  /** The existing section this item shows. */
  section: SectionKey;
  /** Onboarding / Offboarding both live in the existing 'checklists' section, as its ?tab=onboarding|offboarding view. */
  tab?: ChecklistRailTab;
  /** Sections for which this item reads as selected (default: just `section`). "Employee details" covers both personal and employment. */
  alsoActiveFor?: SectionKey[];
}

// Cycle 12 (Owner decision 2026-10-24, "Final Review and HR management"): the rail is ANCHORED, COMPACT and GROUPED instead of one long flat list:
//   Overview | EMPLOYEE RECORD: Employee details (with Personal & contacts and Employment & pay as its sub-destinations), Benefits |
//   ASSIGNMENTS: Onboarding, Documents, Equipment & property | EMPLOYMENT: Offboarding, History | More (collapsed): the remaining destinations.
// Onboarding and Offboarding stay two distinct entries (two views of the one 'checklists' section, ?tab=). No destination, key or URL parameter is removed.
export interface AdminRailChild { id: string; label: string; section: SectionKey }
export interface AdminRailGroupItem extends AdminRailItem { children?: AdminRailChild[] }
export interface AdminRailGroup { id: string; /** null = the ungrouped top entry (no heading). */ label: string | null; items: AdminRailGroupItem[] }

export const ADMIN_EMPLOYEE_RAIL_GROUPS: AdminRailGroup[] = [
  { id: 'top', label: null, items: [{ id: 'overview', label: 'Overview', section: 'overview' }] },
  {
    id: 'record', label: 'Employee record',
    items: [
      {
        id: 'details', label: 'Employee details', section: 'contacts', alsoActiveFor: ['contacts', 'employment'],
        children: [{ id: 'contacts', label: 'Personal & contacts', section: 'contacts' }, { id: 'employment', label: 'Employment & pay', section: 'employment' }],
      },
      { id: 'benefits', label: 'Benefits', section: 'benefits' },
    ],
  },
  {
    id: 'assignments', label: 'Assignments',
    items: [
      { id: 'onboarding', label: 'Onboarding', section: 'checklists', tab: 'onboarding' },
      { id: 'documents', label: 'Documents', section: 'documents' },
      { id: 'property', label: 'Equipment & property', section: 'property' },
    ],
  },
  {
    id: 'employment', label: 'Employment',
    items: [
      { id: 'offboarding', label: 'Offboarding', section: 'checklists', tab: 'offboarding' },
      { id: 'history', label: 'History', section: 'history' },
    ],
  },
];

/** Flat view of the same entries (kept for callers that only need the destinations). */
export const ADMIN_EMPLOYEE_RAIL: AdminRailItem[] = ADMIN_EMPLOYEE_RAIL_GROUPS.flatMap((g) => g.items);

// Owner decision 2026-10-25 ("preview/release parity"): the admin employee rail is a single FLAT,
// readable list with NO faint uppercase intermediate grouping headings, and the identity (name,
// hire date, status) moves OUT of the rail into a separate employee identity card above it. The
// Owner-specified order and labels are exactly:
//   HR overview, Employee details, Benefits, Onboarding, Offboarding, Documents,
//   Equipment & property, History, More (collapsed).
// This narrow preference supersedes the prior grouped-rail design (ADMIN_EMPLOYEE_RAIL_GROUPS,
// retained above only for the flat-destination convenience export and any non-rendering caller).
// Every destination, key, child sub-destination (Personal & contacts / Employment & pay under
// Employee details) and the ?tab= deep link for Onboarding/Offboarding is preserved unchanged —
// only the grouping headings and the identity placement change. Nothing is deleted.
export const ADMIN_EMPLOYEE_FLAT: AdminRailGroupItem[] = [
  { id: 'overview', label: 'HR overview', section: 'overview' },
  {
    id: 'details', label: 'Employee details', section: 'contacts', alsoActiveFor: ['contacts', 'employment'],
    children: [{ id: 'contacts', label: 'Personal & contacts', section: 'contacts' }, { id: 'employment', label: 'Employment & pay', section: 'employment' }],
  },
  { id: 'benefits', label: 'Benefits', section: 'benefits' },
  { id: 'onboarding', label: 'Onboarding', section: 'checklists', tab: 'onboarding' },
  { id: 'offboarding', label: 'Offboarding', section: 'checklists', tab: 'offboarding' },
  { id: 'documents', label: 'Documents', section: 'documents' },
  { id: 'property', label: 'Equipment & property', section: 'property' },
  { id: 'history', label: 'History', section: 'history' },
];

/** Everything else that already exists, in its original order and with its original label. Personal & contacts and Employment & pay now sit under Employee details. */
export const ADMIN_EMPLOYEE_MORE: SectionKey[] = ['time', 'training', 'reviews', 'custom'];
